/**
 * States Component - Empty, Loading, and Error states
 *
 * Reusable state components for empty results, loading skeletons, and error messages.
 *
 * Step 8 of ResultsDemo_BabyStep_Plan implementation
 */

import React from 'react';
import { logger } from '../../../lib/logging';

export interface EmptyStateProps {
  view: 'comparisons' | 'responses';
  onClearFilters?: () => void;
  /** If true, shows "create your first comparison" message. If false, shows "filtered to empty" message. */
  isNewUser?: boolean;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ view, onClearFilters, isNewUser = false }) => {
  React.useEffect(() => {
    logger.info('empty_shown', undefined, {
      op: 'empty_shown',
      component: 'EmptyState',
      view,
      isNewUser
    });
  }, [view, isNewUser]);

  // New user state: prompt to create first comparison
  if (isNewUser && view === 'comparisons') {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 bg-slate-700/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-10 h-10 text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-white mb-3">
          Create your first comparison
        </h3>
        <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
          Get started by comparing responses from multiple AI models to find the best answer for your prompts.
        </p>

        <a
          href="/compare"
          className="inline-block px-6 py-2.5 bg-cyan-500/10 border border-cyan-400/30 rounded-lg text-sm font-medium text-cyan-300 hover:bg-cyan-500/20 transition-all duration-200"
        >
          Create Comparison
        </a>
      </div>
    );
  }

  // Filtered to empty state: suggest clearing filters
  return (
    <div className="text-center py-16">
      <div className="w-20 h-20 bg-slate-700/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <svg
          className="w-10 h-10 text-slate-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {view === 'comparisons' ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          )}
        </svg>
      </div>

      <h3 className="text-xl font-semibold text-white mb-3">
        No {view} found
      </h3>
      <p className="text-slate-400 text-sm mb-6 max-w-md mx-auto">
        Your current filters don't match any {view}. Try adjusting your model selections to see more results.
      </p>

      {onClearFilters && (
        <button
          onClick={onClearFilters}
          className="px-6 py-2.5 bg-cyan-500/10 border border-cyan-400/30 rounded-lg text-sm font-medium text-cyan-300 hover:bg-cyan-500/20 transition-all duration-200"
        >
          Clear Filters
        </button>
      )}
    </div>
  );
};

export interface LoadingStateProps {
  view: 'comparisons' | 'responses';
  count?: number;
}

export const LoadingState: React.FC<LoadingStateProps> = ({ view, count = 6 }) => {
  return (
    <div>
      <div className="mb-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-700/30 rounded mb-2"></div>
        <div className="h-4 w-32 bg-slate-700/20 rounded"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: count }).map((_, idx) => (
          <div
            key={idx}
            className="bg-slate-800/20 border border-slate-700/30 rounded-xl p-6 animate-pulse"
          >
            {/* Header skeleton */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-slate-700/30 rounded-lg"></div>
              <div className="flex-1">
                <div className="h-4 w-24 bg-slate-700/30 rounded mb-1"></div>
                <div className="h-3 w-16 bg-slate-700/20 rounded"></div>
              </div>
            </div>

            {/* Content skeleton */}
            <div className="space-y-2 mb-4">
              <div className="h-3 w-full bg-slate-700/20 rounded"></div>
              <div className="h-3 w-5/6 bg-slate-700/20 rounded"></div>
              <div className="h-3 w-4/6 bg-slate-700/20 rounded"></div>
            </div>

            {/* Footer skeleton */}
            <div className="flex items-center space-x-2">
              <div className="h-8 flex-1 bg-slate-700/20 rounded-lg"></div>
              <div className="h-8 w-10 bg-slate-700/20 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ message, onRetry }) => {
  return (
    <div className="bg-red-500/10 border border-red-400/30 rounded-2xl p-8">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
            <svg
              className="w-6 h-6 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-red-300 mb-2">
            Something went wrong
          </h3>
          <p className="text-sm text-red-200/80 mb-4">
            {message}
          </p>

          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-red-500/20 border border-red-400/30 rounded-lg text-sm font-medium text-red-300 hover:bg-red-500/30 transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
